import { TZDate } from "@date-fns/tz";
import type { TranslateOptions } from "i18n-js";
import { useCallback, useContext, useMemo } from "react";

import { TranslationContext } from "$src/core/contexts/translation";
import useConfig from "$src/core/hooks/use-config";
import type { TranslationKey } from "$src/core/models/translation";
import Logging from "$src/core/services/logging/logging";

/**
 * `Intl.DateTimeFormat` construction is the expensive part of formatting
 * (locale data lookup, pattern compilation). One EPG mount asked for about 190
 * of them. The options set is small and static, so cache per locale + options.
 */
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

const getDateTimeFormat = (
  locale: string,
  options: Intl.DateTimeFormatOptions,
) => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let format = dateTimeFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options);
    dateTimeFormats.set(key, format);
  }
  return format;
};

const useTranslation = () => {
  const config = useConfig();

  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("TranslationContext not found");
  }

  const { i18n } = context;

  const numberFormatter = useMemo(
    () => Intl.NumberFormat(i18n.locale),
    [i18n.locale],
  );

  const updateLocale = useCallback(
    (locale: string) => {
      // eslint-disable-next-line react-hooks/immutability
      i18n.locale = locale;
    },
    [i18n],
  );

  const translate = useCallback(
    (key: TranslationKey, options?: TranslateOptions) =>
      i18n.translate(key, options),
    [i18n],
  );

  const formatDate = useCallback(
    (
      date: string,
      style:
        | "date"
        | "time"
        | "time-hm"
        | "date-time"
        | "short-weekday-day-month" = "date-time",
    ) => {
      const timeZone =
        config.intl.timeZone.type === "fixed"
          ? config.intl.timeZone.value
          : undefined;
      const timeZonedDate = new TZDate(new Date(date), timeZone);

      if (style === "date") {
        return getDateTimeFormat(i18n.locale, {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          timeZone,
        }).format(timeZonedDate);
      }

      if (style === "time") {
        return getDateTimeFormat(i18n.locale, {
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          timeZone,
        }).format(timeZonedDate);
      }

      if (style === "time-hm") {
        return getDateTimeFormat(i18n.locale, {
          hour: "2-digit",
          minute: "2-digit",
          /* h23, not hour12: false — with 2-digit hours some ICU builds resolve to h24 and render midnight as 24:00. */
          hourCycle: "h23",
          timeZone,
        }).format(timeZonedDate);
      }

      if (style === "date-time") {
        return getDateTimeFormat(i18n.locale, {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          timeZone,
        }).format(timeZonedDate);
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (style === "short-weekday-day-month") {
        const weekday = getDateTimeFormat(i18n.locale, {
          weekday: "short",
          timeZone,
        })
          .format(timeZonedDate)
          .replace(".", "");

        const day = getDateTimeFormat(i18n.locale, {
          day: "numeric",
          timeZone,
        }).format(timeZonedDate);

        const month = getDateTimeFormat(i18n.locale, {
          month: "short",
          timeZone,
        }).format(timeZonedDate);

        return `${weekday} ${day} ${month}`;
      }

      Logging.error("Invalid date style", { style });
      return undefined;
    },
    [i18n.locale, config.intl.timeZone],
  );

  const formatNumber = useCallback(
    (number: number) => numberFormatter.format(number),
    [numberFormatter],
  );

  const formatCurrency = useCallback(
    (amount: number, currency?: string) =>
      Intl.NumberFormat(i18n.locale, { style: "currency", currency }).format(
        amount,
      ),
    [i18n.locale],
  );

  return {
    locale: i18n.locale,
    updateLocale,
    t: translate,
    d: formatDate,
    n: formatNumber,
    c: formatCurrency,
  };
};

export default useTranslation;
